import React, { useState, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { X, AlertCircle, CheckCircle2, Search } from 'lucide-react';
import { toast } from 'react-toastify';
import amsAuditCheckpointService, { AmsAuditCheckpoint, UpdateAmsAuditCheckpoint, CreateAmsAuditCheckpoint, ICheckpointMeta } from '../../../services/amsAuditCheckpointService';
import documentMasterService from '../../../services/documentMasterService';
import uomMasterService from '../../../services/uomMasterService';
import Select from '../../../shared/components/form/Select';
import Button from '../../../shared/components/ui/button/Button';

// Document Type Multi-Select Component
interface DocumentTypeMultiSelectProps {
  options: Array<{ value: string; label: string }>;
  selectedValues: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
}

const DocumentTypeMultiSelect: React.FC<DocumentTypeMultiSelectProps> = ({
  options,
  selectedValues,
  onChange,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const allFilteredSelected = filteredOptions.length > 0 &&
    filteredOptions.every(opt => selectedValues.includes(opt.value));

  const handleToggleOption = (value: string) => {
    if (disabled) return;
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    onChange(newValues);
  };

  const handleSelectAll = () => {
    if (disabled) return;
    if (allFilteredSelected) {
      const filteredValues = filteredOptions.map(opt => opt.value);
      const newValues = selectedValues.filter(v => !filteredValues.includes(v));
      onChange(newValues);
    } else {
      const filteredValues = filteredOptions.map(opt => opt.value);
      const newValues = [...new Set([...selectedValues, ...filteredValues])];
      onChange(newValues);
    }
  };

  const selectedOptions = selectedValues
    .map(value => {
      const option = options.find(opt => opt.value === value);
      return option ? { value, label: option.label } : null;
    })
    .filter(Boolean) as Array<{ value: string; label: string }>;

  const handleRemoveTag = (valueToRemove: string, e: React.MouseEvent) => {
    if (disabled) return;
    e.stopPropagation();
    const newValues = selectedValues.filter(v => v !== valueToRemove);
    onChange(newValues);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`min-h-12 w-full rounded-xl border px-4 py-2 text-base text-gray-800 shadow-sm transition-all duration-200 flex items-center justify-between ${
          disabled
            ? 'bg-gray-50 border-gray-300 cursor-not-allowed'
            : 'bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-0 border-gray-300 focus:border-brand-500 focus:ring-brand-500/20'
        }`}
      >
        <div className="flex flex-wrap gap-1.5 flex-1 min-w-0 py-1">
          {selectedOptions.length > 0 ? (
            selectedOptions.map((option) => (
              <span
                key={option.value}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#339274]/10 text-[#339274] rounded-md text-sm font-medium"
                onClick={(e) => e.stopPropagation()}
              >
                {option.label}
                {!disabled && (
                  <button
                    type="button"
                    onClick={(e) => handleRemoveTag(option.value, e)}
                    className="hover:text-red-600 transition-colors flex-shrink-0"
                    aria-label={`Remove ${option.label}`}
                  >
                    <X size={14} />
                  </button>
                )}
              </span>
            ))
          ) : (
            <span className="text-gray-400 py-1">
              {disabled ? 'No document types selected' : 'Select document types'}
            </span>
          )}
        </div>
        {!disabled && (
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ml-2 ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </div>

      {!disabled && isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-80 overflow-hidden">
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search document types..."
                className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {filteredOptions.length > 0 && (
            <div className="p-2 border-b border-gray-200 bg-gray-50">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectAll();
                }}
                className="w-full text-left px-3 py-2 text-sm font-medium text-[#339274] hover:bg-[#339274]/10 rounded-lg transition-colors"
              >
                {allFilteredSelected ? 'Deselect All' : 'Select All'}
              </button>
            </div>
          )}

          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const isSelected = selectedValues.includes(option.value);
                return (
                  <div
                    key={option.value}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleOption(option.value);
                    }}
                    className={`flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer transition-colors ${
                      isSelected ? 'bg-[#339274]/5' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleToggleOption(option.value)}
                      onClick={(e) => e.stopPropagation()}
                      className="h-4 w-4 text-[#339274] focus:ring-[#339274] border-gray-300 rounded cursor-pointer"
                    />
                    <label className="ml-3 text-sm text-gray-700 cursor-pointer flex-1">
                      {option.label}
                    </label>
                  </div>
                );
              })
            ) : (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                No document types found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

interface AmsAuditCheckpointFormProps {
  checkpoint?: AmsAuditCheckpoint; // Optional for create mode
  amsProjectAuditId: string;
  checkpointType: 'project' | 'asset';
  amsProjectAssetId?: string | null; // For asset checkpoints
  projectAssets?: Array<{ _id: string; name: string }>; // Project assets for asset checkpoint dropdown
  auditTypeId?: string; // The audit's auditType id — new custom checkpoints are scoped to just this audit type
  onSuccess: () => void;
  onCancel: () => void;
  mode?: 'create' | 'edit';
}

// Validation schema factory to support both create and edit modes
const createSchema = (isAssetType: boolean, isEditMode: boolean) => yup.object({
  name: yup.string()
    .required('Checkpoint name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(200, 'Name must be less than 200 characters')
    .trim(),
  checkpointMeta: yup.object({
    executionType: yup.string()
      .required('Execution type is required')
      .oneOf(['manual', 'visual', 'hearing'], 'Invalid execution type'),
    documentApplicable: yup.boolean().required(),
    documentTypes: yup.array().of(yup.string().trim().max(100)).optional().default([])
      .test('document-types-required', 'At least one document type must be selected when Document Applicable is checked', function(value) {
        const { documentApplicable } = this.parent;
        if (documentApplicable && (!value || value.length === 0)) {
          return this.createError({ message: 'At least one document type must be selected when Document Applicable is checked' });
        }
        return true;
      }),
    designedParameterAvailable: yup.boolean().required(),
    designedParameterCategory: yup.string().when('designedParameterAvailable', {
      is: true,
      then: (schema) => schema.required('Category is required when Designed Parameter Available is checked').trim().max(100),
      otherwise: (schema) => schema.optional().trim(),
    }),
    designedParameterUom: yup.string().when('designedParameterAvailable', {
      is: true,
      then: (schema) => schema.required('Designed Parameter UOM is required when Designed Parameter Available is checked').trim().max(50),
      otherwise: (schema) => schema.optional().trim(),
    }),
    actualParameter: yup.string().when('designedParameterAvailable', {
      is: true,
      then: (schema) => schema.required('Actual Parameter is required when Designed Parameter Available is checked').trim().max(200),
      otherwise: (schema) => schema.optional().trim(),
    }),
    imagesEnabled: yup.boolean().required(),
    remarksEnabled: yup.boolean().required(),
  }).required(),
  status: yup.string().oneOf(['active', 'inactive']).default('active'),
});

type FormValues = yup.InferType<ReturnType<typeof createSchema>>;

const AmsAuditCheckpointForm: React.FC<AmsAuditCheckpointFormProps> = ({
  checkpoint,
  amsProjectAuditId,
  checkpointType,
  amsProjectAssetId,
  projectAssets = [],
  auditTypeId,
  onSuccess,
  onCancel,
  mode = checkpoint ? 'edit' : 'create',
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [documentTypeOptions, setDocumentTypeOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [uomCategories, setUomCategories] = useState<string[]>([]);
  const [uomOptions, setUomOptions] = useState<Array<{ value: string; label: string }>>([]);

  const isEditMode = mode === 'edit' && checkpoint;

  const defaultFormValues: FormValues = isEditMode ? {
    name: checkpoint.name,
    checkpointMeta: {
      executionType: checkpoint.checkpointMeta.executionType,
      documentApplicable: checkpoint.checkpointMeta.documentApplicable,
      documentTypes: checkpoint.checkpointMeta.documentTypes || [],
      designedParameterAvailable: checkpoint.checkpointMeta.designedParameterAvailable,
      designedParameterCategory: checkpoint.checkpointMeta.designedParameterCategory || '',
      designedParameterUom: checkpoint.checkpointMeta.designedParameterUom || '',
      actualParameter: checkpoint.checkpointMeta.actualParameter || '',
      imagesEnabled: checkpoint.checkpointMeta.imagesEnabled,
      remarksEnabled: checkpoint.checkpointMeta.remarksEnabled,
    },
    status: checkpoint.status,
  } : {
    name: '',
    checkpointMeta: {
      executionType: undefined as any,
      documentApplicable: false,
      documentTypes: [],
      designedParameterAvailable: false,
      designedParameterCategory: '',
      designedParameterUom: '',
      actualParameter: '',
      imagesEnabled: false,
      remarksEnabled: false,
    },
    status: 'active',
  };

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isDirty, isValid },
  } = useForm<FormValues>({
    resolver: yupResolver(createSchema(checkpointType === 'asset', !!checkpoint)) as any,
    mode: 'onChange',
    defaultValues: defaultFormValues,
  });

  // Add assetId field for asset checkpoints (both create and edit mode)
  // In edit mode, extract asset ID from checkpoint.amsProjectAssetId (can be object or string)
  const getInitialAssetId = (): string | null => {
    if (isEditMode && checkpoint) {
      const assetIdValue = checkpoint.amsProjectAssetId;
      if (assetIdValue && typeof assetIdValue === 'object' && !Array.isArray(assetIdValue)) {
        // Object format: { id, name, masterAssetId }
        return (assetIdValue as any).id || (assetIdValue as any)._id || null;
      } else if (typeof assetIdValue === 'string') {
        // String format
        return assetIdValue;
      }
    }
    return amsProjectAssetId || null;
  };
  
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(getInitialAssetId());

  const watchedDocumentApplicable = watch('checkpointMeta.documentApplicable');
  const watchedDesignedParameterAvailable = watch('checkpointMeta.designedParameterAvailable');
  const watchedDesignedParameterCategory = watch('checkpointMeta.designedParameterCategory');
  const watchedDocumentTypes = watch('checkpointMeta.documentTypes') || [];

  useEffect(() => {
    fetchDocumentMasters();
    fetchUomCategories();
  }, []);

  useEffect(() => {
    if (watchedDesignedParameterCategory) {
      fetchUomsByCategory(watchedDesignedParameterCategory);
    } else {
      setUomOptions([]);
      setValue('checkpointMeta.designedParameterUom', '');
    }
  }, [watchedDesignedParameterCategory, setValue]);

  // Update selectedAssetId when checkpoint changes (for edit mode)
  useEffect(() => {
    if (isEditMode && checkpoint && checkpointType === 'asset') {
      const assetIdValue = checkpoint.amsProjectAssetId;
      if (assetIdValue && typeof assetIdValue === 'object' && !Array.isArray(assetIdValue)) {
        // Object format: { id, name, masterAssetId }
        const extractedId = (assetIdValue as any).id || (assetIdValue as any)._id || null;
        setSelectedAssetId(extractedId);
      } else if (typeof assetIdValue === 'string') {
        // String format
        setSelectedAssetId(assetIdValue);
      }
    }
  }, [isEditMode, checkpoint, checkpointType]);

  const fetchDocumentMasters = async () => {
    try {
      const options = await documentMasterService.getDocumentMasterDropdownOptions();
      setDocumentTypeOptions(options);
    } catch (error) {
      console.error('Failed to fetch document masters:', error);
      setDocumentTypeOptions([]);
    }
  };

  const fetchUomCategories = async () => {
    try {
      const categories = await uomMasterService.getCategories();
      setUomCategories(categories);
    } catch (error) {
      console.error('Failed to fetch UOM categories:', error);
      toast.error('Failed to load UOM categories');
      setUomCategories([]);
    }
  };

  const fetchUomsByCategory = async (category: string) => {
    try {
      const options = await uomMasterService.getUomDropdownOptions(category);
      setUomOptions(options);
    } catch (error) {
      console.error('Failed to fetch UOMs:', error);
      toast.error('Failed to load UOMs for selected category');
      setUomOptions([]);
    }
  };

  const onSubmit = async (data: FormValues) => {
    try {
      setIsLoading(true);
      
      if (isEditMode && checkpoint) {
        // Edit mode - try both _id and id fields
        const rawId = (checkpoint as any)._id || (checkpoint as any).id;
        if (!rawId) {
          console.error('Checkpoint missing ID:', checkpoint);
          console.error('Checkpoint object:', JSON.stringify(checkpoint, null, 2));
          toast.error('Checkpoint ID is missing. Please refresh the page.');
          setIsLoading(false);
          return;
        }
        const checkpointId = String(rawId);
        if (checkpointId === 'undefined' || checkpointId === 'null' || checkpointId.trim() === '') {
          console.error('Invalid checkpoint ID format:', checkpointId, 'from checkpoint:', checkpoint);
          toast.error('Invalid checkpoint ID format. Please refresh the page.');
          setIsLoading(false);
          return;
        }
        
        const payload: UpdateAmsAuditCheckpoint = {
          name: data.name.trim(),
          checkpointMeta: {
            executionType: data.checkpointMeta.executionType as 'manual' | 'visual' | 'hearing',
            documentApplicable: data.checkpointMeta.documentApplicable,
            documentTypes: data.checkpointMeta.documentTypes || [],
            designedParameterAvailable: data.checkpointMeta.designedParameterAvailable,
            designedParameterCategory: data.checkpointMeta.designedParameterCategory || '',
            designedParameterUom: data.checkpointMeta.designedParameterUom || '',
            actualParameter: data.checkpointMeta.actualParameter || '',
            imagesEnabled: data.checkpointMeta.imagesEnabled,
            remarksEnabled: data.checkpointMeta.remarksEnabled,
            // Preserve the checkpoint's existing audit-type applicability — this form
            // doesn't expose editing it.
            applicableAuditTypeIds: checkpoint.checkpointMeta.applicableAuditTypeIds || [],
          },
          status: data.status as 'active' | 'inactive',
          // Include asset ID if it's an asset checkpoint
          ...(checkpointType === 'asset' ? { amsProjectAssetId: selectedAssetId || null } : {}),
        };
        await amsAuditCheckpointService.updateCheckpoint(checkpointId, payload);
        toast.success('Checkpoint updated successfully');
      } else {
        // Create mode
        // For custom checkpoints, we need a masterAmsCheckpointId
        // Using a placeholder ID - in production, you might want to create a dummy master checkpoint
        const placeholderMasterCheckpointId = '000000000000000000000000';
        
        // Set the appropriate audit type flag based on auditType
        // In create mode, the values are set via useEffect based on auditType
        // In edit mode, preserve existing values from the form
        const checkpointMeta: ICheckpointMeta = {
          executionType: data.checkpointMeta.executionType as 'manual' | 'visual' | 'hearing',
          documentApplicable: data.checkpointMeta.documentApplicable,
          documentTypes: data.checkpointMeta.documentTypes || [],
          designedParameterAvailable: data.checkpointMeta.designedParameterAvailable,
          designedParameterCategory: data.checkpointMeta.designedParameterCategory || '',
          designedParameterUom: data.checkpointMeta.designedParameterUom || '',
          actualParameter: data.checkpointMeta.actualParameter || '',
          imagesEnabled: data.checkpointMeta.imagesEnabled,
          remarksEnabled: data.checkpointMeta.remarksEnabled,
          // A custom checkpoint created from within a specific audit only applies to
          // that audit's audit type.
          applicableAuditTypeIds: auditTypeId ? [auditTypeId] : [],
        };
        
        const payload: CreateAmsAuditCheckpoint = {
          amsProjectAuditId,
          amsProjectAssetId: checkpointType === 'asset' ? (selectedAssetId || null) : null,
          masterAmsCheckpointId: placeholderMasterCheckpointId,
          name: data.name.trim(),
          checkpointType,
          checkpointMeta,
        };
        await amsAuditCheckpointService.createCheckpoint(payload);
        toast.success('Checkpoint created successfully');
      }
      onSuccess();
    } catch (error: any) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} checkpoint:`, error);
      toast.error(error?.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} checkpoint`);
    } finally {
      setIsLoading(false);
    }
  };

  const executionTypeOptions = [
    { value: 'manual', label: 'Manual' },
    { value: 'visual', label: 'Visual' },
    { value: 'hearing', label: 'Hearing' },
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8" onClick={(e) => e.stopPropagation()}>
      {/* Basic Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Checkpoint Name - Always First */}
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Checkpoint Name *
          </label>
          <div className="relative">
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${
                    errors.name
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
                      : field.value && !errors.name
                      ? 'border-green-300 focus:border-green-500 focus:ring-green-500/20'
                      : 'border-gray-300 focus:border-brand-500 focus:ring-brand-500/20'
                  }`}
                  placeholder="Enter checkpoint name"
                  maxLength={200}
                />
              )}
            />
            {watch('name') && !errors.name && (
              <CheckCircle2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-green-500" />
            )}
            {errors.name && (
              <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500" />
            )}
          </div>
          {errors.name && (
            <p className="flex items-center gap-1 text-sm text-red-600">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {errors.name.message}
            </p>
          )}
        </div>

        {/* Project Asset - For asset type in both create and edit mode */}
        {checkpointType === 'asset' && (
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Project Asset {!isEditMode && '*'}
            </label>
            <div className="relative">
              <Select
                value={selectedAssetId || ''}
                onChange={(option) => setSelectedAssetId(option?.value || null)}
                options={projectAssets.map(asset => ({
                  value: asset._id,
                  label: asset.name,
                }))}
                placeholder="Select project asset"
                className="h-12"
              />
              {!isEditMode && !selectedAssetId && (
                <p className="flex items-center gap-1 text-sm text-red-600 mt-1">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  Project asset is required
                </p>
              )}
            </div>
          </div>
        )}

        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Execution Type *
          </label>
          <div className="relative">
            <Controller
              name="checkpointMeta.executionType"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value}
                  onChange={(option) => field.onChange(option?.value || '')}
                  options={executionTypeOptions}
                  placeholder="Select execution type"
                  className="h-12"
                />
              )}
            />
            {errors.checkpointMeta?.executionType && (
              <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500 pointer-events-none" />
            )}
          </div>
          {errors.checkpointMeta?.executionType && (
            <p className="flex items-center gap-1 text-sm text-red-600">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {errors.checkpointMeta.executionType.message}
            </p>
          )}
        </div>
      </div>

      {/* Checkpoint Meta */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="flex items-center">
          <Controller
            name="checkpointMeta.documentApplicable"
            control={control}
            render={({ field }) => (
              <input
                type="checkbox"
                id="documentApplicable"
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
                className="h-4 w-4 text-[#339274] focus:ring-[#339274] border-gray-300 rounded"
              />
            )}
          />
          <label htmlFor="documentApplicable" className="ml-2 block text-sm font-semibold text-gray-700">
            Document Applicable
          </label>
        </div>

        <div></div>

        {watchedDocumentApplicable && (
          <div className="col-span-2 space-y-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Document Types *
            </label>
            <div className="relative">
              <Controller
                name="checkpointMeta.documentTypes"
                control={control}
                render={({ field }) => (
                  <DocumentTypeMultiSelect
                    options={documentTypeOptions}
                    selectedValues={watchedDocumentTypes || []}
                    onChange={(values) => {
                      field.onChange(values);
                    }}
                  />
                )}
              />
              {errors.checkpointMeta?.documentTypes && (
                <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500 pointer-events-none" />
              )}
            </div>
            {errors.checkpointMeta?.documentTypes && (
              <p className="flex items-center gap-1 text-sm text-red-600">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {errors.checkpointMeta.documentTypes.message}
              </p>
            )}
          </div>
        )}

        <div className="flex items-center">
          <Controller
            name="checkpointMeta.designedParameterAvailable"
            control={control}
            render={({ field }) => (
              <input
                type="checkbox"
                id="designedParameterAvailable"
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
                className="h-4 w-4 text-[#339274] focus:ring-[#339274] border-gray-300 rounded"
              />
            )}
          />
          <label htmlFor="designedParameterAvailable" className="ml-2 block text-sm font-semibold text-gray-700">
            Designed Parameter Available
          </label>
        </div>

        {watchedDesignedParameterAvailable && (
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Designed Parameter Category <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Controller
                name="checkpointMeta.designedParameterCategory"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onChange={(option) => field.onChange(option?.value || '')}
                    options={uomCategories.map(cat => ({ value: cat, label: cat }))}
                    placeholder="Select Category"
                    className="h-12"
                  />
                )}
              />
              {errors.checkpointMeta?.designedParameterCategory && (
                <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500 pointer-events-none" />
              )}
            </div>
            {errors.checkpointMeta?.designedParameterCategory && (
              <p className="flex items-center gap-1 text-sm text-red-600">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {errors.checkpointMeta.designedParameterCategory.message}
              </p>
            )}
          </div>
        )}

        {watchedDesignedParameterAvailable && watchedDesignedParameterCategory && (
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Designed Parameter UOM <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Controller
                name="checkpointMeta.designedParameterUom"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onChange={(option) => field.onChange(option?.value || '')}
                    options={uomOptions}
                    placeholder="Select UOM"
                    className="h-12"
                  />
                )}
              />
              {errors.checkpointMeta?.designedParameterUom && (
                <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500 pointer-events-none" />
              )}
            </div>
            {errors.checkpointMeta?.designedParameterUom && (
              <p className="flex items-center gap-1 text-sm text-red-600">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {errors.checkpointMeta.designedParameterUom.message}
              </p>
            )}
          </div>
        )}

        {watchedDesignedParameterAvailable && (
          <div className="col-span-2 space-y-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Actual Parameter <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Controller
                name="checkpointMeta.actualParameter"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="text"
                    placeholder="Enter actual parameter"
                    maxLength={200}
                    className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${
                      errors.checkpointMeta?.actualParameter
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
                        : 'border-gray-300 focus:border-brand-500 focus:ring-brand-500/20'
                    }`}
                  />
                )}
              />
              {errors.checkpointMeta?.actualParameter && (
                <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500 pointer-events-none" />
              )}
            </div>
            {errors.checkpointMeta?.actualParameter && (
              <p className="flex items-center gap-1 text-sm text-red-600">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {errors.checkpointMeta.actualParameter.message}
              </p>
            )}
          </div>
        )}

        <div className="flex items-center">
          <Controller
            name="checkpointMeta.imagesEnabled"
            control={control}
            render={({ field }) => (
              <input
                type="checkbox"
                id="imagesEnabled"
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
                className="h-4 w-4 text-[#339274] focus:ring-[#339274] border-gray-300 rounded"
              />
            )}
          />
          <label htmlFor="imagesEnabled" className="ml-2 block text-sm font-semibold text-gray-700">
            Images Enabled
          </label>
        </div>

        <div className="flex items-center">
          <Controller
            name="checkpointMeta.remarksEnabled"
            control={control}
            render={({ field }) => (
              <input
                type="checkbox"
                id="remarksEnabled"
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
                className="h-4 w-4 text-[#339274] focus:ring-[#339274] border-gray-300 rounded"
              />
            )}
          />
          <label htmlFor="remarksEnabled" className="ml-2 block text-sm font-semibold text-gray-700">
            Remarks Enabled
          </label>
        </div>

      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t mt-6">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isLoading || !isDirty || !isValid || (checkpointType === 'asset' && !isEditMode && !selectedAssetId)}
          className="bg-[#339274] hover:bg-[#29795f] text-white"
        >
          {isLoading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Checkpoint' : 'Create Checkpoint')}
        </Button>
      </div>
    </form>
  );
};

export default AmsAuditCheckpointForm;
